import { Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { Card } from '@/components/layout/Card/Card';
import { TableHeaderBar } from '@/components/tables/components/TableHeaderBar/TableHeaderBar';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { getIntegrationProviderName } from '@/modules/household/LinkHouseholdDataTrowser/DirectHoldingsStep/hooks/directHoldingsStepData.utils';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import {
  EntityBusinessesTableRow,
  getIntegratedBusinessesFromBusinesses,
  getManuallyTrackedBusinessesFromBusinesses,
  mapBusinessesToRows,
  useColumns,
} from './HoldingsGroupBusinesses.utils';

const MAX_ROW_COUNT = 10;

export interface HoldingsGroupBusinessesProps {
  businesses: EntityBusinessesTableRow[];
}

export function HoldingsGroupBusinesses({
  businesses,
}: HoldingsGroupBusinessesProps) {
  const integratedBusinesses = useMemo(
    () => getIntegratedBusinessesFromBusinesses(businesses),
    [businesses]
  );
  const manuallyTrackedBusinesses = useMemo(
    () => getManuallyTrackedBusinessesFromBusinesses(businesses),
    [businesses]
  );

  const totalValue = useMemo(
    () =>
      businesses.reduce(
        (sum, business) => sum.plus(business.rawValue || new Decimal(0)),
        new Decimal(0)
      ),
    [businesses]
  );

  const hasIntegratedBusinesses = integratedBusinesses.length > 0;
  const hasManuallyTrackedBusinesses = manuallyTrackedBusinesses.length > 0;

  if (!hasManuallyTrackedBusinesses && !hasIntegratedBusinesses) {
    return null;
  }

  return (
    <Card variant="outlined">
      <Stack
        sx={{
          p: 2,
          borderRadius: '4px',
        }}
      >
        <Stack gap={2}>
          <Stack>
            <Typography variant="title-s">Owned businesses</Typography>
            <Typography variant="body-xs">
              {formatCurrencyNoDecimals(totalValue)} total
            </Typography>
          </Stack>
          {hasManuallyTrackedBusinesses && (
            <HoldingsGroupBusinessesInner
              businesses={manuallyTrackedBusinesses}
              variant="manually-tracked"
            />
          )}
          {hasIntegratedBusinesses && (
            <HoldingsGroupBusinessesInner
              businesses={integratedBusinesses}
              variant="integrated"
            />
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

type HoldingsGroupBusinessesInnerPropsVariant =
  | 'manually-tracked'
  | 'integrated';

export interface HoldingsGroupBusinessesInnerProps {
  businesses: EntityBusinessesTableRow[];
  variant: HoldingsGroupBusinessesInnerPropsVariant;
}

export function HoldingsGroupBusinessesInner({
  businesses,
  variant,
}: HoldingsGroupBusinessesInnerProps) {
  const { householdIntegrationLink, householdId } =
    useHouseholdDetailsContext();
  const columns = useColumns(householdId!);
  const rows = useMemo(() => mapBusinessesToRows(businesses), [businesses]);

  return (
    <Stack>
      <TableHeaderBar
        header={
          variant === 'manually-tracked'
            ? 'Manually tracked businesses'
            : `Businesses linked to ${getIntegrationProviderName(householdIntegrationLink)}`
        }
      />
      <DataTable
        hideFooterPagination={rows.length <= MAX_ROW_COUNT}
        pageSize={MAX_ROW_COUNT}
        pagination={true}
        rows={rows}
        columns={columns}
        getRowHeight={() => 60}
        paginationMode="client"
        noPointer
        rowHoverVariant="transparent"
      />
    </Stack>
  );
}
